import React, { memo, useEffect, useRef, useState } from "react";

const MapOfWorld = memo(function MapOfWorld(props) {
  const chartRef = useRef(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isChartDrawn, setIsChartDrawn] = useState(false);
  const [error, setError] = useState(null);

  // Load Google Charts API
  useEffect(() => {
    // Only load once
    if (isScriptLoaded) return;

    try {
      // Check if Google Charts is already loaded
      if (window.google && window.google.visualization) {
        setIsScriptLoaded(true);
        return;
      }

      // Create script element to load Google Charts
      const script = document.createElement("script");
      script.src = "https://www.gstatic.com/charts/loader.js";
      script.async = true;
      script.onload = () => {
        setIsScriptLoaded(true);
      };
      script.onerror = () => {
        setError("Failed to load map visualization");
      };
      document.head.appendChild(script);

      // Clean up
      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    } catch (err) {
      console.error("Error loading Google Charts:", err);
      setError("Error initializing map");
    }
  }, [isScriptLoaded]);

  // Initialize and draw the chart when data or script loading status changes
  useEffect(() => {
    if (!isScriptLoaded || isChartDrawn || error) return;

    const containerElement = document.getElementById("geo-chart-container");
    if (!containerElement) return;

    const drawChart = () => {
      try {
        const { countryData = [] } = props;

        if (!countryData.length) {
          setError("No data available");
          return;
        }

        // Calculate proper dimensions for the container
        const containerWidth = containerElement.offsetWidth;
        const idealHeight = containerWidth / 2;

        // Create a DataTable for countries
        const dataTable = new window.google.visualization.DataTable();

        // Add columns
        dataTable.addColumn("string", "Country"); // Country name
        dataTable.addColumn("number", "Page Views"); // Page Views value

        // Add country data
        countryData.forEach((country) => {
          dataTable.addRow([country.country, country.value]);
        });

        // Format numbers with commas
        const numberFormatter = new google.visualization.NumberFormat({
          pattern: "#,###",
        });
        numberFormatter.format(dataTable, 1);

        // Configure chart options
        const options = {
          // Style for regions/countries
          colorAxis: {
            colors: ["#c6dbef", "#6baed6", "#2171b5", "#084594"],
            minValue: 0,
            maxValue: Math.max(...countryData.map((country) => country.value)) || 100,
          },
          backgroundColor: "#f8f8f8",
          datalessRegionColor: "#f0f0f0",
          defaultColor: "#f0f0f0",

          // Layout settings
          keepAspectRatio: true,
          width: containerWidth,
          height: idealHeight,
          chartArea: {
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
          },

          // Map behavior
          legend: "none",
          tooltip: {
            textStyle: {
              color: "#333333",
              fontName: "Arial, sans-serif",
              fontSize: 14,
              bold: false,
              italic: false,
            },
            showColorCode: false,
            trigger: "focus",
          },
          enableRegionInteractivity: true,
          resolution: "countries",
          displayMode: "regions",
        };

        // Create and draw the chart
        const chart = new window.google.visualization.GeoChart(containerElement);

        // Add event listener for chart completion
        google.visualization.events.addOneTimeListener(chart, "ready", function () {
        });

        chart.draw(dataTable, options);
        setIsChartDrawn(true);
      } catch (err) {
        console.error("Error drawing map:", err);
        setError("Error rendering map");
      }
    };

    // Load required visualization libraries
    window.google.charts.load("current", {
      packages: ["geochart"],
    });

    window.google.charts.setOnLoadCallback(() => {
      drawChart();
    });
  }, [props.countryData, isScriptLoaded, isChartDrawn, error]);

  // Apply custom CSS
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      #geo-chart-container {
        width: 100% !important;
        overflow: hidden;
        position: relative;
      }
      #geo-chart-container > div {
        width: 100% !important;
      }
      #geo-chart-container svg {
        overflow: visible !important;
      }
      
      /* Custom tooltip styling */
      .google-visualization-tooltip {
        background-color: rgba(255, 255, 255, 0.95) !important;
        color: #333 !important;
        padding: 12px 16px !important;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
        font-size: 14px !important;
        border: none !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2) !important;
        transition: opacity 0.2s ease-in-out !important;
        z-index: 1000 !important;
        max-width: 250px !important;
        text-align: center !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (isChartDrawn) {
        setIsChartDrawn(false); // Force redraw
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isChartDrawn]);

  if (!props.countryData) {
    return (
      <div className="YearLineChart">
        <NoDataAvailable name={props.name} />
      </div>
    );
  }

  return (
    <div className="MonthLineChart" style={{ background: "#333", position: "relative" }}>
      <div
        style={{
          color: "#FFF",
          textAlign: "center",
          fontSize: "20px",
          fontWeight: "bold",
          textShadow: "2px 2px 4px #000000",
          paddingBottom: "5px",
        }}>
        <u>{props.name}</u>
        {error && <div style={{ fontSize: "12px", color: "#FF0000" }}>{error}</div>}
      </div>

      <div
        id="geo-chart-container"
        ref={chartRef}
        style={{
          width: "100%",
          position: "relative",
          background: "#f8f8f8",
          overflow: "hidden",
        }}>
        {!isScriptLoaded && (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#666",
              minHeight: "200px",
            }}>
            Loading map...
          </div>
        )}
      </div>
    </div>
  );
});

function NoDataAvailable(props) {
  return (
    <div
      style={{
        position: "relative",
        width: "500px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#FFFFFF",
        height: "100%",
        fontSize: "15px",
      }}>
      <div>
        {props.name} - <span style={{ color: "#FF0000" }}>No Data Available</span>
      </div>
    </div>
  );
}

export default MapOfWorld;
